import React, { createContext, useContext } from 'react';
import { useSearchQuery } from '../SearchRequestProvider';
import { SearchQueryQuery } from 'generated/graphql';
import { createFeature } from '@edreamsodigeo/frontend-core';
import { useFlowTools } from 'flowTools/useFlowTools';
import ItinerarySearchResponseProvider from './ItinerarySearchResponseProvider';
import SearchItinerariesResponseProvider from './SearchItinerariesResponseProvider';
import ItinerarySearchResponseNoShoppingCartProvider from './ItinerarySearchResponseNoShoppingCartProvider';
import {
  META_LANDING_IN_OF2_MOBILE,
  META_LANDING_IN_OF2_DESKTOP,
  SEARCH_ITINERARY_META,
  SEARCH_ITINERARIES,
} from 'clientState/ab/aliases';
import { useAlternativeItineraryMutation } from 'clientState/providers/AlternativeItineraryMutationProvider';
import {
  SearchResponseProviderProps,
  SearchResponseContextType,
  SearchResponseActionsContextType,
} from './types';

const LOADING_STATE = {
  loading: true,
  data: null,
  error: null,
  extensions: null,
};
const DEFAULT_ACTIONS = {
  onNoSearchResults: /* istanbul ignore next */ (): void => {},
  onSearchParamsError: /* istanbul ignore next */ (): void => {},
};

export const replaceItinerariesById = (
  itineraries,
  alternativesItineraries
) => {
  if (!alternativesItineraries || alternativesItineraries.length === 0) {
    return itineraries;
  }
  return itineraries.map((itinerary) => {
    const match = alternativesItineraries.find(
      (newItem) => newItem.id === itinerary.id
    );
    return match ? match : itinerary;
  });
};

export const SearchResponseContext =
  createContext<SearchResponseContextType>(LOADING_STATE);
export const SearchResponseActionsContext =
  createContext<SearchResponseActionsContextType>(DEFAULT_ACTIONS);

export const useSearchResponseQuery = (): SearchResponseContextType =>
  useContext(SearchResponseContext);

export const useSearchResponseActions = (): SearchResponseActionsContextType =>
  useContext(SearchResponseActionsContext);

export const useDefaultFeeType = ():
  | SearchQueryQuery['search']['defaultFeeType']
  | null => {
  const { error, loading, data } = useSearchResponseQuery();
  if (error || loading) {
    return null;
  }
  return data?.search.defaultFeeType;
};

export const useTrackingInfo = ():
  | SearchQueryQuery['search']['trackingInfo']
  | null => {
  const { error, loading, data } = useSearchResponseQuery();
  if (error || loading) {
    return null;
  }
  return data?.search.trackingInfo;
};

export const useSearchId = ():
  | SearchQueryQuery['search']['searchId']
  | null => {
  const { loading, error, data } = useSearchResponseQuery();
  const { alternativeSearchId } = useAlternativeItineraryMutation();
  if (loading || error) {
    return null;
  }
  return alternativeSearchId || data?.search.searchId || null;
};

const useCampaignItineraryIds = (campaignId: string): string[] => {
  const { loading, error, data } = useSearchResponseQuery();
  if (loading || error) {
    return [];
  }

  return (
    data?.search.campaignsItineraryLists?.find(
      (campaign) => campaign.campaignKey === campaignId
    )?.campaignItineraryIds || []
  );
};

const HOTEL_VOUCHER_CAMPAIGN_ID = 'hotel-voucher';
export const useHotelVoucherItineraryIds = (): string[] =>
  useCampaignItineraryIds(HOTEL_VOUCHER_CAMPAIGN_ID);

const FLIGHT_OF_THE_DAY_CAMPAIGN_ID = 'flight-of-the-day';
export const useFlightOfTheDayItineraryIds = (): string[] =>
  useCampaignItineraryIds(FLIGHT_OF_THE_DAY_CAMPAIGN_ID);

const ItinerarySearchMigrationAB = createFeature({
  name: SEARCH_ITINERARY_META,
  variations: [
    ItinerarySearchResponseNoShoppingCartProvider,
    SearchItinerariesResponseProvider,
  ],
  LoadingComponent: () => null,
  version: 1,
});
const ItinerarySearchResponseMobileProvider = createFeature({
  name: META_LANDING_IN_OF2_MOBILE,
  variations: [ItinerarySearchResponseProvider, ItinerarySearchMigrationAB],
  LoadingComponent: () => null,
  version: 1,
});
const ItinerarySearchResponseDesktopProvider = createFeature({
  name: META_LANDING_IN_OF2_DESKTOP,
  variations: [ItinerarySearchResponseProvider, ItinerarySearchMigrationAB],
  LoadingComponent: () => null,
  version: 1,
});

const SearchItinerariesResponseProviderVersioned: React.FC<SearchResponseProviderProps> =
  createFeature({
    name: SEARCH_ITINERARIES,
    variations: [
      ItinerarySearchResponseProvider,
      (props) => <SearchItinerariesResponseProvider {...props} />,
    ],
    LoadingComponent: () => null,
    version: 1,
  });

const ItinerarySearchResponseProviderVersioned: React.FC<
  SearchResponseProviderProps
> = (props) => {
  const { isMobile } = useFlowTools();
  const searchRequest = useSearchQuery();
  const isMeta = searchRequest?.itinerary?.externalSelection;

  if (!isMeta) {
    return <SearchItinerariesResponseProviderVersioned {...props} />;
  } else {
    return isMobile ? (
      <ItinerarySearchResponseMobileProvider {...props} />
    ) : (
      <ItinerarySearchResponseDesktopProvider {...props} />
    );
  }
};

export default ItinerarySearchResponseProviderVersioned;
